/**
 * ChipMultiSelect: seletor compacto de vários itens (tags ou etapas).
 *
 * Nasceu em 2026-09-30 para acabar com a "parede de chips": telas que
 * desenhavam o catálogo INTEIRO como chips clicáveis (conta com ~100 tags
 * virava centenas de chips nas automações da etapa, no público da campanha e
 * na régua). Aqui só o que está SELECIONADO vira chip; o resto fica atrás de
 * um botão que abre busca + lista rolável.
 *
 * Controlado e agnóstico de catálogo: quem chama carrega as opções e guarda os
 * ids. Não cria item nem grava nada (para tag do contato existe o
 * TagCombobox, que persiste direto no contato).
 *
 * Props:
 *  - options   -> [{ id, name, color?, group? }]; `group` agrupa na lista
 *                 (ex.: nome do funil para etapas)
 *  - value     -> ids selecionados (string[])
 *  - onChange(ids)
 *  - colors    -> tema
 *  - addLabel  -> texto do botão (default "Adicionar")
 *  - searchPlaceholder
 *  - emptyText -> quando não há opção nenhuma no catálogo
 *  - noneText  -> quando nada está selecionado (opcional)
 *  - accent    -> cor fixa dos chips selecionados (ex.: vermelho no "excluir");
 *                 sem ela, cada chip usa a cor do próprio item
 *  - disabled  -> só mostra os selecionados, sem remover nem adicionar
 *  - inlineThreshold -> até N opções desenha os chips direto (catálogo
 *                 pequeno não precisa de busca); default 0 = sempre compacto
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, Search, X } from 'lucide-react';

const norm = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const ChipMultiSelect = ({
  options = [],
  value = [],
  onChange,
  colors,
  addLabel = 'Adicionar',
  searchPlaceholder = 'Buscar...',
  emptyText = 'Nada para escolher ainda.',
  noneText = '',
  accent = null,
  disabled = false,
  inlineThreshold = 0,
  maxListHeight = 220,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const border = colors.borderColor || colors.border;
  const selected = useMemo(() => new Set(value || []), [value]);
  const byId = useMemo(() => {
    const map = {};
    for (const o of options) map[o.id] = o;
    return map;
  }, [options]);

  const toggle = (id) => {
    if (disabled) return;
    const next = selected.has(id)
      ? (value || []).filter((x) => x !== id)
      : [...(value || []), id];
    onChange?.(next);
  };

  // Fecha ao clicar fora e no Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQuery('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return options;
    return options.filter((o) => norm(o.name).includes(q) || norm(o.group).includes(q));
  }, [options, query]);

  // Agrupa preservando a ordem de chegada (grupo vazio = sem cabeçalho).
  const grouped = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const o of filtered) {
      const g = o.group || '';
      if (!map.has(g)) { map.set(g, []); order.push(g); }
      map.get(g).push(o);
    }
    return order.map((g) => ({ group: g, items: map.get(g) }));
  }, [filtered]);

  const colorOf = (o) => accent || o?.color || colors.accentPrimary;

  const chip = (o, { removable }) => {
    const c = colorOf(o);
    return (
      <span
        key={o.id}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%',
          padding: '3px 6px 3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600,
          border: `1px solid ${c}`, backgroundColor: `${c}1f`, color: c,
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: o.color || c, flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name || 'Sem nome'}</span>
        {removable && (
          <button
            type="button"
            onClick={() => toggle(o.id)}
            title="Remover"
            aria-label={`Remover ${o.name || ''}`}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: '50%', border: 'none', padding: 0,
              background: 'transparent', color: 'inherit', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={11} />
          </button>
        )}
      </span>
    );
  };

  if (options.length === 0) {
    return <span style={{ fontSize: 12, color: colors.textTertiary || colors.textSecondary }}>{emptyText}</span>;
  }

  // Catálogo pequeno: chips diretos (um clique liga e desliga).
  if (options.length <= inlineThreshold) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const on = selected.has(o.id);
          const c = colorOf(o);
          return (
            <button
              key={o.id}
              type="button"
              disabled={disabled}
              aria-pressed={on}
              onClick={() => toggle(o.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer',
                border: `1px solid ${on ? c : border}`,
                backgroundColor: on ? `${c}1f` : 'transparent',
                color: on ? c : colors.textSecondary,
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: o.color || c }} />
              {o.name || 'Sem nome'}
            </button>
          );
        })}
      </div>
    );
  }

  const selectedOptions = (value || []).map((id) => byId[id]).filter(Boolean);

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
        {selectedOptions.map((o) => chip(o, { removable: !disabled }))}
        {selectedOptions.length === 0 && noneText && (
          <span style={{ fontSize: 12, color: colors.textTertiary || colors.textSecondary }}>{noneText}</span>
        )}
        {!disabled && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              cursor: 'pointer',
              border: `1px dashed ${open ? colors.accentPrimary : border}`,
              backgroundColor: open ? `${colors.accentPrimary}12` : 'transparent',
              color: open ? colors.accentPrimary : colors.textSecondary,
            }}
          >
            <Plus size={12} /> {addLabel}
          </button>
        )}
      </div>

      {open && !disabled && (
        <div
          style={{
            border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden',
            backgroundColor: colors.bgPrimary,
          }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
            borderBottom: `1px solid ${border}`, backgroundColor: colors.bgSecondary,
          }}>
            <Search size={13} color={colors.textTertiary || colors.textSecondary} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                fontSize: 13, color: colors.textPrimary, fontFamily: 'inherit',
              }}
            />
            <span style={{ fontSize: 11, color: colors.textTertiary || colors.textSecondary, whiteSpace: 'nowrap' }}>
              {selected.size} de {options.length}
            </span>
          </div>
          <div style={{ maxHeight: maxListHeight, overflowY: 'auto' }} role="listbox" aria-multiselectable="true">
            {filtered.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: colors.textSecondary }}>
                Nada encontrado para "{query}".
              </div>
            )}
            {grouped.map(({ group, items }) => (
              <div key={group || '_'}>
                {group && (
                  <div style={{
                    padding: '6px 12px 3px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.4px',
                    textTransform: 'uppercase', color: colors.textTertiary || colors.textSecondary,
                  }}>{group}</div>
                )}
                {items.map((o) => {
                  const on = selected.has(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => toggle(o.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '7px 12px', border: 'none', textAlign: 'left', cursor: 'pointer',
                        backgroundColor: on ? `${colors.accentPrimary}10` : 'transparent',
                        color: colors.textPrimary, fontSize: 13, fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: o.color || colors.accentPrimary, flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.name || 'Sem nome'}
                      </span>
                      {on && <Check size={14} color={colors.accentPrimary} style={{ flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 10px', borderTop: `1px solid ${border}`, backgroundColor: colors.bgSecondary,
          }}>
            <button
              type="button"
              onClick={() => onChange?.([])}
              disabled={selected.size === 0}
              style={{
                border: 'none', background: 'transparent', padding: 0, fontSize: 12,
                color: selected.size === 0 ? (colors.textTertiary || colors.textSecondary) : colors.textSecondary,
                cursor: selected.size === 0 ? 'default' : 'pointer',
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                border: 'none', background: 'transparent', padding: 0, fontSize: 12, fontWeight: 600,
                color: colors.accentPrimary, cursor: 'pointer',
              }}
            >
              Concluir
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChipMultiSelect;
